import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const RoleSettings = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({
    role: "",
    description: "",
    permissions: [],
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const { fetchWithRetry, user, isLoading } = useAuth();
  const navigate = useNavigate();

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/admin/roles",
      });
      setRoles(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Lỗi tải danh sách vai trò:", error);
      setError(error.response?.data?.message || "Không thể tải danh sách vai trò!");
      setLoading(false);
    }
  }, [fetchWithRetry]);

  const handleAddRole = useCallback(async () => {
    if (!newRole.role) {
      setError("Vui lòng nhập tên vai trò!");
      return;
    }
    try {
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/roles",
        data: newRole,
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setNewRole({ role: "", description: "", permissions: [] });
      setShowAddModal(false);
      await loadRoles();
    } catch (error) {
      console.error("Lỗi thêm vai trò:", error);
      setError(error.response?.data?.message || "Không thể thêm vai trò!");
      setTimeout(() => setError(null), 3000);
    }
  }, [newRole, loadRoles, fetchWithRetry]);

  const handleEditRole = useCallback(async () => {
    try {
      const response = await fetchWithRetry({
        method: "put",
        url: `/api/admin/roles/${editingRole._id}`,
        data: {
          role: editingRole.role,
          permissions: editingRole.permissions,
          description: editingRole.description,
        },
      });
      setSuccessMessage(response.data.message);
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingRole(null);
      setShowEditModal(false);
      await loadRoles();
    } catch (error) {
      console.error("Lỗi cập nhật vai trò:", error);
      setError(error.response?.data?.message || "Không thể cập nhật vai trò!");
      setTimeout(() => setError(null), 3000);
    }
  }, [editingRole, loadRoles, fetchWithRetry]);

  const handleDeleteRole = useCallback(
    async (id) => {
      if (!window.confirm("Bạn có chắc muốn xóa vai trò này?")) return;
      try {
        const response = await fetchWithRetry({
          method: "delete",
          url: `/api/admin/roles/${id}`,
        });
        setSuccessMessage(response.data.message);
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadRoles();
      } catch (error) {
        console.error("Lỗi xóa vai trò:", error);
        setError(error.response?.data?.message || "Không thể xóa vai trò!");
        setTimeout(() => setError(null), 3000);
      }
    },
    [fetchWithRetry, loadRoles]
  );

  useEffect(() => {
    if (isLoading) return;

    if (!user || !user.permissions.includes("manage_roles")) {
      setError("Bạn không có quyền truy cập trang này!");
      navigate("/login");
      return;
    }
    loadRoles();
  }, [user, isLoading, loadRoles, navigate]);

  // Phân loại quyền theo nhóm
  const permissionGroups = [
    {
      groupName: "Quyền người dùng",
      permissions: [
        { name: "view_users", description: "Xem danh sách người dùng" },
        { name: "edit_users", description: "Chỉnh sửa thông tin người dùng" },
        { name: "delete_users", description: "Xóa người dùng khỏi hệ thống" },
        { name: "send_messages", description: "Gửi tin nhắn tới người dùng" },
        { name: "view_profiles", description: "Xem hồ sơ cá nhân của người dùng" },
        { name: "send_gifts", description: "Gửi quà tặng cho người dùng khác" },
        { name: "view_hidden_profiles", description: "Xem hồ sơ ẩn của người dùng" },
      ],
    },
    {
      groupName: "Quyền quản trị",
      permissions: [
        { name: "manage_roles", description: "Quản lý vai trò và quyền hạn" },
        { name: "view_reports", description: "Xem báo cáo từ người dùng" },
        { name: "delete_reports", description: "Xóa báo cáo" },
        { name: "ban_users", description: "Khóa tài khoản người dùng" },
        { name: "promote_users", description: "Nâng cấp vai trò của người dùng" },
        { name: "manage_payment_gateways", description: "Quản lý cổng thanh toán" },
        { name: "manage_coins", description: "Quản lý Coins" },
        { name: "manage_gifts", description: "Quản lý Gifts" },
        { name: "manage_chat_restrictions", description: "Giới hạn Chat" },
        
        
      ],
    },
    {
      groupName: "Quyền nâng cao",
      permissions: [
        { name: "manage_matches", description: "Quản lý ghép đôi giữa người dùng" },
        { name: "view_premium_features", description: "Truy cập các tính năng cao cấp" },
        { name: "moderate_content", description: "Kiểm duyệt nội dung (bài viết, ảnh, bình luận)" },
        { name: "manage_events", description: "Tạo, chỉnh sửa, xóa sự kiện hẹn hò/kết bạn" },
        { name: "access_analytics", description: "Xem dữ liệu phân tích (thống kê hoạt động)" },
        { name: "view_private_messages", description: "Xem tin nhắn riêng tư để kiểm duyệt/hỗ trợ" },
        { name: "configure_settings", description: "Cấu hình cài đặt hệ thống (quy tắc, giới hạn)" },
        { name: "manage_membership_plans", description: "Quản trị hệ thống các gói " },
        { name: "view_payments", description: "Quán lý thanh toán" },

        { name: "manage_logs", description: "Xem và xóa các log hệ thống" },
        { name: "view_system_messages", description: "Xem tin nhắn hệ thống" },
        { name: "view_user_activity", description: "Xem hoạt động của người dùng" },
        { name: "view_user_profiles", description: "Xem hồ sơ cá nhân của người dùng" },
        { name: "view_user_messages", description: "Xem tin nhắn của người dùng" },
        { name: "view_user_gifts", description: "Xem quà tặng của người dùng" },
        { name: "view_user_matches", description: "Xem các ghép đôi của người dùng" },
        { name: "view_user_events", description: "Xem các sự kiện hen hò/kết bạn của người dùng" },
        { name: "view_user_logs", description: "Xem các log của người dùng" },
        { name: "view_user_system_messages", description: "Xem tin nhắn hệ thống của người dùng" },
        { name: "view_user_user_activity", description: "Xem hoạt động của người dùng khác" },
        { name: "view_user_user_profiles", description: "Xem hồ sơ cá nhân của người dùng khác" },
        { name: "view_user_user_messages", description: "Xem tin nhắn của người dùng khác" },
        { name: "view_user_user_gifts", description: "Xem quà tặng của người dùng khác" },
        
      ],
    },
  ];

  const roleSuggestions = ["user", "vip", "mod", "admin", "matchmaker", "support", "premium"];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Quản lý vai trò</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
          disabled={loading}
        >
          + Thêm vai trò
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && (
        <p className={`mb-4 ${successMessage.includes("thành công") ? "text-green-500" : "text-red-500"}`}>
          {successMessage}
        </p>
      )}

      {loading ? (
        <p className="text-center text-gray-500">Đang tải...</p>
      ) : roles.length === 0 ? (
        <p className="text-center text-gray-500">Không có vai trò nào để hiển thị.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <th className="p-4 text-left font-semibold">Tên vai trò</th>
                <th className="p-4 text-left font-semibold">Mô tả</th>
                <th className="p-4 text-left font-semibold">Quyền</th>
                <th className="p-4 text-left font-semibold">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role._id} className="border-b hover:bg-gray-50 transition duration-200">
                  <td className="p-4 text-gray-800">{role.role}</td>
                  <td className="p-4 text-gray-600">{role.description || "Không có mô tả"}</td>
                  <td className="p-4 text-gray-600">{role.permissions.join(", ") || "Không có quyền"}</td>
                  <td className="p-4 space-x-2">
                    <button
                      onClick={() => {
                        setEditingRole(role);
                        setShowEditModal(true);
                      }}
                      className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition duration-200"
                    >
                      Sửa
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role._id)}
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition duration-200"
                    >
                      Xóa
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Thêm vai trò mới</h3>
            <input
              type="text"
              placeholder="Tên vai trò (ví dụ: matchmaker, support)"
              value={newRole.role}
              onChange={(e) => setNewRole({ ...newRole, role: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              list="roleSuggestions"
            />
            <datalist id="roleSuggestions">
              {roleSuggestions.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
            <input
              type="text"
              placeholder="Mô tả"
              value={newRole.description}
              onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mb-3">
              <label className="block text-gray-700 font-medium mb-2">Quyền:</label>
              {permissionGroups.map((group) => (
                <div key={group.groupName} className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">{group.groupName}:</h4>
                  <div className="flex flex-wrap gap-2">
                    {group.permissions.map((perm) => (
                      <label key={perm.name} className="flex items-center" title={perm.description}>
                        <input
                          type="checkbox"
                          checked={newRole.permissions.includes(perm.name)}
                          onChange={(e) => {
                            const updatedPermissions = e.target.checked
                              ? [...newRole.permissions, perm.name]
                              : newRole.permissions.filter((p) => p !== perm.name);
                            setNewRole({ ...newRole, permissions: updatedPermissions });
                          }}
                          className="mr-2"
                        />
                        {perm.name}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200"
              >
                Hủy
              </button>
              <button
                onClick={handleAddRole}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingRole && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Chỉnh sửa vai trò</h3>
            <input
              type="text"
              value={editingRole.role}
              disabled
              className="w-full p-2 mb-3 border rounded bg-gray-100 focus:outline-none"
            />
            <input
              type="text"
              placeholder="Mô tả"
              value={editingRole.description}
              onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mb-3">
              <label className="block text-gray-700 font-medium mb-2">Quyền:</label>
              {permissionGroups.map((group) => (
                <div key={group.groupName} className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">{group.groupName}:</h4>
                  <div className="flex flex-wrap gap-2">
                    {group.permissions.map((perm) => (
                      <label key={perm.name} className="flex items-center" title={perm.description}>
                        <input
                          type="checkbox"
                          checked={editingRole.permissions.includes(perm.name)}
                          onChange={(e) => {
                            const updatedPermissions = e.target.checked
                              ? [...editingRole.permissions, perm.name]
                              : editingRole.permissions.filter((p) => p !== perm.name);
                            setEditingRole({ ...editingRole, permissions: updatedPermissions });
                          }}
                          className="mr-2"
                        />
                        {perm.name}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setEditingRole(null);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200"
              >
                Hủy
              </button>
              <button
                onClick={handleEditRole}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
              >
                Cập nhật
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleSettings;